import { Box, Link, MenuItem, SxProps } from "@mui/material";
import EdmIconButton from "atoms/Buttons/EdmIconButton";
import DownloadIcon from "icons/Download.svg";
import { MouseEvent, ReactElement, useCallback } from "react";

import { EdmButton } from "../../atoms";
import { apiEndpoint } from "../../misc/settings";

const buildQueryParams = (fileIds: number[], paramName = "generated_documents[]") => {
  const queryParams = [];
  for (const fileId of fileIds) {
    queryParams.push(`${paramName}=${fileId}`);
  }
  return queryParams.join("&");
};

export const buildGeneratedDocumentDownloadUrl = (fileIds: number[], paramName = "generated_documents[]") => {
  return `${apiEndpoint}/download_generated_document/?${buildQueryParams(fileIds, paramName)}`;
};

export type GeneratedDocumentDownloadButtonPropsType = {
  ids: number[];
  icon?: ReactElement | null;
  label?: string;
  fileParamName?: string;
  onClick?: () => void;
  mode?: "link" | "menuitem" | "icon";
  disabled?: boolean;
  sx?: SxProps;
};

export default function GeneratedDocumentDownloadButton(props: GeneratedDocumentDownloadButtonPropsType) {
  const {
    ids,
    fileParamName = "generated_documents[]",
    icon = <DownloadIcon />,
    label = undefined,
    onClick,
    mode = "button",
    disabled = false,
    sx = {},
  } = props;

  const handleClick = useCallback(
    (event: MouseEvent) => {
      window.open(buildGeneratedDocumentDownloadUrl(ids, fileParamName));
      onClick?.();
      event.stopPropagation();
    },
    [ids, fileParamName, onClick],
  );

  const getComponent = useCallback(() => {
    if (mode == "link") {
      return (
        <Link onClick={handleClick} sx={{ cursor: "pointer", ...sx }}>
          {label}
        </Link>
      );
    } else if (mode == "menuitem") {
      return (
        <MenuItem disabled={disabled} sx={sx}>
          <Box sx={{ cursor: "pointer" }} onClick={handleClick}>
            {icon || <></>} {label}
          </Box>
        </MenuItem>
      );
    } else if (mode == "icon") {
      return (
        <EdmIconButton onClick={handleClick} disabled={disabled} sx={sx}>
          {icon}
        </EdmIconButton>
      );
    } else {
      return (
        <EdmButton onClick={handleClick} label={label} disabled={disabled} color="info" sx={sx}>
          {icon ? icon : <></>}
        </EdmButton>
      );
    }
  }, [mode, label, icon, disabled, sx, handleClick]);

  return getComponent();
}
